import { Box, IconButton, Skeleton, Stack, Tooltip, Typography, useTheme, Grid } from "@mui/material";
import { ButtonComponent } from "../../components/form";
import { useState, useCallback, useEffect } from "react";
import SnackbarComponent from "../../components/snackBar/snackbarComponent";
import DialogComponent from "../../components/dialog/dialogComponent";
import { GetPastTrips } from "../../context/trip/tripProvider";
import ErrorView from "../error/errorView";
import { useNavigate } from "react-router-dom";
import { useGetAllSavedActivityAndSiteMutation } from "../../services/sitesAndActivities";
import { useDeleteAllToursMutation } from "../../services/tripApi";
import { GetAuth } from "../../context/auth/authProvider";
import { DataGrid } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import CalculatorResults from "../calculator/sitesAndActivites/calculatorResults";
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RecordSearch from "./recordSearch";
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import subDays from 'date-fns/subDays';
import { numberWithCommas } from "../../utils/customFunctions";
import { getTotalFee } from "../calculator/sitesAndActivites/sitesAndActivitiesUtils";
import { getModifiedDate } from "./pastToursList";

const limitedDateRange = (date) => {
  const today = new Date();
  const minDate = subDays(today, 365);
  const maxDate = today;
  
  // Disable dates before the minDate or after the maxDate
  return date < minDate || date > maxDate;
};

const PastSitesAndActivitiesList = () => {
    const theme = useTheme();

    const [showDialog, setShowDialog] = useState(false);

    const [snackBar, setSnackBar] = useState({
        message: "",
        open: false,
        color: ""
    });

    const [selectedFilterDateRange, setSelectedFilterDateRange] = useState(null);//[date, date]
    const [searchKey, setSearchKey] = useState(null);

    const trip = GetPastTrips();

    const navigate = useNavigate();

    const auth = GetAuth();

    const staffId = auth?.currentUser?.staffID;

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);

    const [totalPages, setTotalPages] = useState(0);
    const [numberOfElements, setNumberOfElements] = useState(0);
    const [siteAndActivityData, setSiteAndActivityData] = useState([]);

    const [
        getAllSavedActivitiesAndSites,
        {
            isLoading: isLoadingActivitiesAndSites,
            isSuccess: isSuccessEditActivitiesAndSites,
            data: activitiesAndSitesData,
            isError: isErrorEditActivitiesAndSites,
            error: errorEditActivitiesAndSites 
        }
    ] = useGetAllSavedActivityAndSiteMutation();

    useEffect(() => {
        if(selectedFilterDateRange && selectedFilterDateRange.length === 2){
            getAllSavedActivitiesAndSites({
                page: page, 
                size: pageSize, 
                staffId,
                fromDate: getModifiedDate(selectedFilterDateRange[0]),
                toDate: getModifiedDate(selectedFilterDateRange[1]),
                searchKey: searchKey
            });
        }
        else{
            getAllSavedActivitiesAndSites({
                page: page, 
                size: pageSize, 
                staffId,
                searchKey: searchKey
            });
        }
    }, [page, pageSize, staffId]);

    const loadAllSiteAndActivity = useCallback(() => {
        if(page > 0){
            setPage(0);
        }
        else{
            if(selectedFilterDateRange && selectedFilterDateRange.length === 2){
                getAllSavedActivitiesAndSites({
                    page: page, 
                    size: pageSize, 
                    staffId,
                    fromDate: getModifiedDate(selectedFilterDateRange[0]),
                    toDate: getModifiedDate(selectedFilterDateRange[1]),
                    searchKey: searchKey
                });
            }
            else{
                getAllSavedActivitiesAndSites({
                    page: page, 
                    size: pageSize, 
                    staffId,
                    searchKey: searchKey
                });
            }
        }
    }, [page, pageSize, selectedFilterDateRange, searchKey, staffId]);

    useEffect(() => {
        if(isSuccessEditActivitiesAndSites){
            console.log("Sites and Activities...");
            console.log(activitiesAndSitesData);

            setTotalPages(activitiesAndSitesData.data.totalPages);
            setSiteAndActivityData(activitiesAndSitesData.data.content);
            setNumberOfElements(activitiesAndSitesData.data.totalElements);
        }
    }, [isSuccessEditActivitiesAndSites, activitiesAndSitesData]);

    useEffect(() => {
        if(isErrorEditActivitiesAndSites){
            console.log("Tours Error...");
            console.log(errorEditActivitiesAndSites);
        }
    }, [isErrorEditActivitiesAndSites, errorEditActivitiesAndSites]);

    const clearAllRequest = useCallback(() => {
        setShowDialog(true);
    }, []);

    const [
        deleteAllTours,
        {
            isLoading: isLoadingDeleteAllTours,
            isSuccess: isSuccessDeleteAllTours,
            isError: isErrorDeleteAllTours,
            error: errorDeleteAllTours
        }
    ] = useDeleteAllToursMutation();

    const clearAll = useCallback(() => {
        setShowDialog(false);

        deleteAllTours({staffId});
    }, [staffId]);

    useEffect(() => {
        if(isSuccessDeleteAllTours){
            setSnackBar({
                message: "All trips cleared successfully!",
                open: true,
                color: "green"
            });

            loadAllSiteAndActivity();
        }
        else if(isErrorDeleteAllTours){
            console.log("Tours Delete Error...");
            console.log(errorDeleteAllTours);

            if(errorDeleteAllTours.originalStatus === 200){
                setSnackBar({
                    message: "All trips cleared successfully!",
                    open: true,
                    color: "green"
                });

                loadAllSiteAndActivity();
            }
            else{
                setSnackBar({
                    message: errorDeleteAllTours?.data?.message ?? "Something went wrong",
                    open: true,
                    color: "red"
                });
            }
        }
    }, [isSuccessDeleteAllTours, isErrorDeleteAllTours, errorDeleteAllTours]);

    const changePages = useCallback((event, page) => {
        setPage(page - 1);
    }, []);

    const changePageAndSize = useCallback((newPage, newPageSize) => {
        setPage(newPage);
        setPageSize(newPageSize);
    }, []);

    const selectDateFilterRange = useCallback((value, event) => {
        if(value && value.length === 2){
            getAllSavedActivitiesAndSites({
                page: page, 
                size: pageSize, 
                staffId,
                fromDate: getModifiedDate(value[0]),
                toDate: getModifiedDate(value[1]),
                searchKey: searchKey
            });
        }
        else{
            getAllSavedActivitiesAndSites({
                page: page, 
                size: pageSize, 
                staffId,
                searchKey: searchKey
            });
        }

        setSelectedFilterDateRange(value);
    }, [page, pageSize, searchKey, staffId]);

    const searchWithKeywords = useCallback((keyword) => {
        if(selectedFilterDateRange && selectedFilterDateRange.length === 2){
            getAllSavedActivitiesAndSites({
                page: page, 
                size: pageSize, 
                staffId,
                fromDate: getModifiedDate(selectedFilterDateRange[0]),
                toDate: getModifiedDate(selectedFilterDateRange[1]),
                searchKey: keyword
            });
        }
        else{
            getAllSavedActivitiesAndSites({
                page: page, 
                size: pageSize, 
                staffId,
                searchKey: keyword
            });
        }
    }, [page, pageSize, selectedFilterDateRange, staffId]);

    const backOption =  <Stack
        direction='row'
        justifyContent='end'
        alignItems='center'
        width='100%'
        spacing={1}
        sx={{
            mb: {xs: 2, md: 0}
        }}
    >
        <ButtonComponent 
            label='New Plan'
            onClick={() => {
                navigate('/calculator?type=sites_activities');
            }}
            sx={{
                width: 'fit-content',
                fontWeight: 500,
                fontSize: {xs: 12, md: 16},
                letterSpacing: 0.36,
                p: {xs: 1, md: 1.5}
            }}
            variant="outlined"
            startIcon={<AddCircleIcon />}
        />
    </Stack>

    const title = <Stack
        sx={{
            width: '100%'
        }}
    >
        <Typography
            fontWeight={800}
            fontSize={{xs: 16, md: 40}}
            letterSpacing={0.36}
            color={theme.palette.primary.main}
            textAlign={{xs: 'left', md: 'center'}}
        >
            Sites & Activities
        </Typography>
    </Stack>

    let body;

    if(isLoadingActivitiesAndSites){
        body = <Box
            px={{xs: 2, md: 7}}
            pb={{xs: 15, md: 0}}
        >
            <Stack
                direction='column'
                justifyContent='start'
                alignItems='center'
                width='100%'
                height='100vh'
                overflow='hidden'
                py={{xs: 2, md: 9}}
            >
                {backOption}

                {title}

                <Stack
                    direction='column'
                    width='100%'
                    spacing={{xs: 1, md: 2.5}}
                    pt={{xs: 3, md: 7}}
                    pb={{xs: 1, md: 4}}
                >
                    {
                        [0,1,2].map((item, index) => {
                            return <Skeleton 
                                key={index}
                                variant="rounded" 
                                width='100%' 
                                height={80} 
                            />
                        })
                    }
                </Stack>
            </Stack>
        </Box>
    }
    else if(isErrorEditActivitiesAndSites){
        body = <ErrorView 
            icon="/images/error.png"
            errorTitle={errorEditActivitiesAndSites?.status ?? "Unknown Error"}
            errorDescription={errorEditActivitiesAndSites?.data?.message ?? "Something went wrong!"}
        />
    }
    else if(isSuccessEditActivitiesAndSites){
        body = <></>
    }
    
    return (
        <>
            <Box 
                px={{xs: 2, lg: 7}}
                pb={{xs: 15, md: 0}}
            >
                <Stack
                    direction='column'
                    justifyContent='start'
                    alignItems='center'
                    width='100%'
                    py={{xs: 2, md: 9}}
                    data-aos="fade-down"
                >
                    {title}

                    {backOption}

                    <Stack
                        direction='column'
                        justifyContent='center'
                        alignItems='center'
                        sx={{
                            pt: {xs: 1, md: 3},
                            pb: {xs: 1, md: 4}
                        }}
                        width='100%'
                    >
                        {/* {body} */}

                        <Grid
                            direction='row'
                            width='100%'
                            spacing={{xs: 0.5, md: 1}}
                            sx={{mb: {xs: 2, md: 3}}}
                            container
                        >
                            <Grid size={{xs: 12, md: 8}}>
                                <RecordSearch
                                    searchAPICall={searchWithKeywords}
                                    setSearchKey={setSearchKey}
                                />
                            </Grid>

                            <Grid size={{xs: 12, md: 4}}>
                                <DateRangePicker 
                                    container={() => document.body} 
                                    preventOverflow={true}
                                    size="lg"
                                    placeholder="Select Date Range"
                                    format="MM/dd/yyyy" 
                                    character=" – "
                                    value={selectedFilterDateRange}
                                    onChange={selectDateFilterRange}
                                    showOneCalendar
                                    shouldDisableDate={limitedDateRange}
                                    block
                                />
                            </Grid>
                        </Grid>

                        <PastSitesAndActivitiesTableList 
                            siteAndActivityData={siteAndActivityData}
                            dataLoading={isLoadingActivitiesAndSites}
                            totalPages={numberOfElements}
                            page={page}
                            pageSize={pageSize}
                            changePageAndSize={changePageAndSize}

                            loadAllSiteAndActivity={loadAllSiteAndActivity}

                            clearAllRequest={clearAllRequest}
                            isLoadingDeleteAllTours={isLoadingDeleteAllTours}
                        />
                    </Stack>
                </Stack>
            </Box>

            <DialogComponent 
                open={showDialog}
                title="Clear"
                body={<>
                    <Stack
                        direction='column'
                        justifyContent='center'
                        alignItems='center'
                    >
                        <Typography
                            fontWeight={300}
                            fontSize={14}
                            textAlign='justify'
                        >
                            Are you sure to clear all the trip details?
                        </Typography>
                    </Stack>
                </>}
                handleClose={() => setShowDialog(false)}
                proceedText="No"
                cancelText="Yes"
                proceedCallback={() => setShowDialog(false)}
                cancelCallback={clearAll}
            />

            <SnackbarComponent 
                message={snackBar.message}
                color={snackBar.color}
                open={snackBar.open}
                handleClose={() => setSnackBar({
                    message: "",
                    open: false
                })}
            />
        </>
    );
}

export default PastSitesAndActivitiesList;

const PastSitesAndActivitiesTableList = ({
    siteAndActivityData,
    dataLoading,
    totalPages,
    page,
    pageSize,
    changePageAndSize,

    loadAllSiteAndActivity,

    clearAllRequest,
    isLoadingDeleteAllTours
}) => {
    const [selectedSiteAndActivity, setSelectedSiteAndActivity] = useState(null);

    const viewSiteAndActivityDetails = useCallback((id) => {
        for(let i = 0; i < siteAndActivityData.length; i++){
            if(siteAndActivityData[i].id === id){
                setSelectedSiteAndActivity(siteAndActivityData[i]);
                break;
            }
        }
    }, [siteAndActivityData]);

    const columns = [
        { 
            field: 'lastUpdatedOn', 
            headerName: 'Updated On', 
            description: 'The date any changes were made to the tour sites/activities calculated values', 
            width: 125,
            renderCell: (params) => {
                return params?.row?.lastUpdatedOn?.split('T')[0].replaceAll('-', '/');
            }
        },
        { 
            field: 'name', 
            headerName: 'Name', 
            flex: 1,
            renderCell: (params) => {
                return <Tooltip 
                    title={params?.row?.name}
                    enterDelay={0}
                >
                    {params?.row?.name}
                </Tooltip>;
            }
        },
        { 
            field: 'kidsCount', 
            headerName: 'No. of Kids', 
            width: 150
        },
        { 
            field: 'adultCount', 
            headerName: 'No. of Adults', 
            width: 125
        },
        { 
            field: '', 
            headerName: 'Total Fee', 
            width: 125,
            renderCell: (params) => {
                return `$ ${numberWithCommas(getTotalFee(params?.row?.siteFees ?? [], params?.row?.activityFees ?? [], params?.row?.kidsCount, params?.row?.adultCount))}`
            }
        },
        { 
            field: 'id', 
            headerName: '', 
            width: 50,
            renderCell: (params) => {
                return <Stack
                    direction='row'
                    alignItems='center'
                    height='100%'
                >
                    <IconButton aria-label="view" size="small" onClick={() => viewSiteAndActivityDetails(params.id)}>
                        <Tooltip title="View">
                            <VisibilityIcon fontSize="small"/>
                        </Tooltip>
                    </IconButton>
                </Stack>
            }
        }
    ];

    const [showDetails, setShowDetails] = useState(false);

    const updatePagination = useCallback((model, details) => {
        console.log(model.page);
        console.log(model.pageSize);

        changePageAndSize(model.page, model.pageSize);
    }, []);

    const selectRow = useCallback((rowModel, details) => {
        const rowId = rowModel.row.id;

        viewSiteAndActivityDetails(rowId);
    }, [viewSiteAndActivityDetails]);

    useEffect(() => {
        if(selectedSiteAndActivity){
            setShowDetails(true);
        }
    }, [selectedSiteAndActivity, showDetails]);

    const closeDetails = useCallback(() => {
        setShowDetails(false);
        setSelectedSiteAndActivity(false);
    }, []);

    return (
        <>
            <Paper sx={{ width: '100%' }}>
                <DataGrid
                    rows={siteAndActivityData}
                    columns={columns}
                    rowCount={totalPages}
                    initialState={{ 
                        pagination: {
                            paginationModel: { page: page, pageSize: pageSize }
                        }
                    }}
                    pageSizeOptions={[25, 50, 100]}
                    pagination
                    paginationMode="server"
                    sx={{ 
                        border: 0,
                        '& .MuiDataGrid-cell:focus': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        // Optional: remove the outline from column headers when focused
                        '& .MuiDataGrid-columnHeader:focus': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeader:focus-within': {
                            outline: 'none',
                        }
                    }}
                    disableColumnMenu
                    disableColumnSorting
                    loading={dataLoading}
                    onPaginationModelChange={updatePagination}
                    onRowClick={selectRow}
                />
            </Paper>

            {/* <ButtonComponent 
                label='Clear All'
                onClick={clearAllRequest}
                sx={{
                    mt: {xs: 3, md: 5},
                    display: siteAndActivityData?.length > 0 ? 'flex' : 'none'
                }}
                variant="outlined"
                inLoading={isLoadingDeleteAllTours}
                startIcon={<DeleteForeverIcon sx={{color: 'red'}}/>}
            /> */}

            {/* View Details */}
            <DialogComponent 
                open={showDetails}
                body={
                    <>
                        <CalculatorResults 
                            backToFilterView={() => {}}
                            summaryData={selectedSiteAndActivity}
                            loadAllTours={loadAllSiteAndActivity}
                            modelViewClose={closeDetails}
                            sx={{
                                width: {xs: '100%', md: '75vw', lg: '50vw'}
                            }}
                        />
                    </>
                }
                handleClose={closeDetails}
                hideAction
                size="xl"
            />
        </>
    );
}